import React, { useEffect, useState } from 'react';
import {
  DynamicValue,
  Element,
  ElementFactory,
} from '@fulcrumapp/fulcrum-core';
import AddItemMenu from './add-item-menu';
import ChecklistEditorItem from './checklist-editor-item';

interface Props {
  value: DynamicValue;
  onChange: (value: DynamicValue) => void;
}

const MAX_CHECKLIST_ITEMS = 100;

const defaultElements = {
  YesNoField: {
    type: 'YesNoField',
    key: null,
    label: '',
    description: null,
    required: false,
    disabled: false,
    hidden: false,
    data_name: null,
    default_value: null,
    positive: { label: 'Yes', value: 'yes' },
    negative: { label: 'No', value: 'no' },
    neutral: { label: 'N/A', value: 'n/a' },
    neutral_enabled: true,
    default_previous_value: false,
  },
  CheckboxField: {
    type: 'CheckboxField',
    key: null,
    label: '',
    description: null,
    required: false,
    disabled: false,
    hidden: false,
    data_name: null,
    default_value: null,
    default_previous_value: false,
  },
};

const defaultValuesForTypes = {
  YesNoField: null,
  CheckboxField: false,
};

const ChecklistEditor = ({ value, onChange }: Props) => {
  const checklistItemValue = value.items[0] || value.createNewItem();
  const [elements, setElements] = useState(checklistItemValue.elements);

  useEffect(() => {
    const newElements = elements.filter((element: Element) => element.label.length > 0);
    if (newElements.length === 0) {
      onChange(new DynamicValue(value.element, []));
      return;
    }

    const newElementsJSON = newElements.map((element: Element) => element.toJSON());
    const newValuesJSON = newElements.reduce((values: object, element: Element) => {
      const existingValue = checklistItemValue.values.get(element.key);
      if (existingValue) {
        values[element.key] = existingValue.toJSON();
      } else {
        values[element.key] = defaultValuesForTypes[element.type];
      }
      return values;
    }, {});

    const newChecklistItemValue = {
      metadata: checklistItemValue.metadata,
      elements: newElementsJSON,
      values: newValuesJSON,
    };
    onChange(new DynamicValue(value.element, [newChecklistItemValue]));
  }, [elements]);

  const generateIdentifier = () => {
    const exclude = elements.map((element: Element) => element.key);
    let id: string|null = null;
    while (id === null || exclude.includes(id)) {
      // This is how fulcrum editor currently creates ids, using that implementation for consistency
      // eslint-disable-next-line no-bitwise
      id = (((1 + Math.random()) * 0x10000) | 0).toString(16).substring(1);
    }
    return id;
  };

  const createElement = (type: string, overrides: any = {}) => ElementFactory.create(null, {
    ...(defaultElements[type]),
    ...overrides,
  });

  const addField = (type: string) => {
    // TODO: show some feedback to the user when they exceed max checklist items
    if (elements.length >= MAX_CHECKLIST_ITEMS) {
      return;
    }

    const newElement = createElement(type, {
      key: generateIdentifier(),
      data_name: generateIdentifier(),
    });
    setElements([...elements, newElement]);
  };

  const handleAddItem = (type: string) => {
    addField(type);
  };

  const handleDeleteField = (key: string) => {
    setElements(elements.filter((element: Element) => element.key !== key));
  };

  const handleChangeFieldLabel = (key: string, label: string) => {
    setElements(elements.map((element: Element) => {
      if (element.key === key) {
        return createElement(element.type, {
          label,
          key: element.key,
          data_name: element.dataName,
        });
      }
      return element;
    }));
  };

  const handleChangeFieldType = (key: string, type: string) => {
    // TODO Add back when we add yes no fields
    // const index = elements.findIndex((element) => element.key === key);
    // const newElement = createElement(type, {
    //   key: generateIdentifier(),
    //   data_name: generateIdentifier(),
    // });
    // const updatedElements = elements;
    // updatedElements.splice(index, 1, newElement);
    // setElements(updatedElements);
  };

  const handlePressEnter = () => {
    addField('CheckboxField');
  };

  if (elements.length === 0) {
    addField('CheckboxField');
  }

  return (
    <>
      {
        elements.map((element: Element) => (
          <ChecklistEditorItem
            key={element.key}
            element={element}
            elementCount={elements.length}
            onChangeLabel={handleChangeFieldLabel}
            onDelete={handleDeleteField}
            onChangeType={handleChangeFieldType}
            onPressEnter={handlePressEnter}
          />
        ))
      }
      <AddItemMenu onAddItem={handleAddItem} />
    </>
  );
};

export default ChecklistEditor;
